import { CodetableScreenName } from 'app/area/codetable/types-store';
import { CodeEditSectionType, CodetableScreen } from 'app/area/codetable/item/types';
import { ScreenTemplate } from 'app/area/codetable/item/screen-renderer/screen-template';
import { Severity } from 'app/area/codetable/item/screen-renderer/screen-pickers/consequence-severity/severity';
import { High } from 'app/area/codetable/item/screen-renderer/screen-fields/consequence-severity/high';
import { Recurrence } from 'app/area/codetable/item/screen-renderer/screen-pickers/consequence-recurrence/recurrence';
import { Likelihood } from 'app/area/codetable/item/screen-renderer/screen-pickers/consequence-likelihood/likelihood';
import { UoM } from 'app/area/codetable/item/screen-renderer/screen-pickers/consequence-severity/uom';
import { Low } from 'app/area/codetable/item/screen-renderer/screen-fields/consequence-severity/low';
import { Mid } from 'app/area/codetable/item/screen-renderer/screen-fields/consequence-severity/mid';
import { ScoreCalcWeight } from 'app/area/codetable/item/screen-renderer/screen-fields/check-list/score-calc-weight';
import { CompanyRegisterId } from 'app/area/codetable/item/screen-renderer/screen-fields/company/company-register-id';
import { SupportedCodeScreens } from 'app/area/codetable/item/screen-renderer/types';
import { validateConsequenceSeverity } from 'app/area/codetable/item/screen-renderer/screen-validate/consequence-severity/validate';
import { validateConsequenceRecurrence } from 'app/area/codetable/item/screen-renderer/screen-validate/consequence-recurrence/validate';
import { validateConsequenceLikelihood } from 'app/area/codetable/item/screen-renderer/screen-validate/consequence-likelihood/validate';
import { ConsequencesScreen } from 'app/area/codetable/item/screen-renderer/custom-screens/consequences/consequences-screen';
import { validateChecklistHierarchy } from 'app/area/codetable/item/screen-renderer/screen-validate/check-list-hierarchy/validate';
import { CodetableTranslation } from 'app/area/codetable/constants';
import { ReferenceScreen } from 'app/area/codetable/item/screen-renderer/custom-screens/reference/reference-screen';
import { CheckedCode } from 'app/area/codetable/item/screen-renderer/screen-pickers/reference/checked-code';
import { UncheckedCode } from 'app/area/codetable/item/screen-renderer/screen-pickers/reference/unchecked-code';
import { MinLevel } from 'app/area/codetable/item/screen-renderer/screen-fields/cause-category/min-level';
import {
  consequencesLikelihoodConfiguration, consequencesRecurrenceConfiguration, consequencesSeverityConfiguration
} from 'app/area/codetable/item/screen-renderer/screen-configuration/consequences-severity';
import {
  validateSubstanceCategory, validateSubstanceHierarchy, validateUom
} from 'app/area/codetable/item/screen-renderer/screen-validate/uom/validate';
import { RecurrenceLevel } from 'app/area/codetable/item/screen-renderer/screen-fields/recurrence-level/recurrence-level';
import { SeverityLevel } from 'app/area/codetable/item/screen-renderer/screen-fields/severity-level/severity-level';
import { LikelihoodLevel } from 'app/area/codetable/item/screen-renderer/screen-fields/likelihood-level/likelihood-level';
import { validateLevel } from 'app/area/codetable/item/screen-renderer/screen-validate/level/validate';
import { RangeFrom } from 'app/area/codetable/item/screen-renderer/screen-fields/integrity-status/range-from';
import { RangeTo } from 'app/area/codetable/item/screen-renderer/screen-fields/integrity-status/range-to';
import { validateReference } from 'app/area/codetable/item/screen-renderer/screen-validate/reference/validate';
import { DefaultUoMType } from 'app/area/codetable/item/screen-renderer/screen-pickers/substance/details/default-uom-type';
import { DefaultUoM } from 'app/area/codetable/item/screen-renderer/screen-pickers/substance/details/default-uom';
import { UomSets } from 'app/area/codetable/item/screen-renderer/screen-pickers/substance/sets/uom-sets';
import { SubstanceCategoryTemplate } from 'app/area/codetable/item/screen-renderer/custom-screens/substance/substance-category-screen';
import { SubstanceHierarchyTemplate } from 'app/area/codetable/item/screen-renderer/custom-screens/substance/substance-hierarchy-screen';
import { substanceHierarchyConfiguration } from 'app/area/codetable/item/screen-renderer/screen-configuration/substance-hierarchy';
import { substanceCategoryConfiguration } from 'app/area/codetable/item/screen-renderer/screen-configuration/substance-category';
import { uomSetConfiguration } from 'app/area/codetable/item/screen-renderer/screen-configuration/uom-set';
import { UomSetTemplate } from 'app/area/codetable/item/screen-renderer/custom-screens/uom-set/uom-set-screen';
import { UomTypeTo } from 'app/area/codetable/item/screen-renderer/screen-pickers/uom-conversion-types/uom-type-to';
import { UomTypeFrom } from 'app/area/codetable/item/screen-renderer/screen-pickers/uom-conversion-types/uom-type-from';
import { validateUnitOfMeasureConversionType } from 'app/area/codetable/item/screen-renderer/screen-validate/uom-conversion-types/validate';
import { InsuranceTypeConsequenceType } from 'app/area/codetable/item/screen-renderer/screen-pickers/insurance-type/insurance-type-consequence-type';
import { UomTemplate } from 'app/area/codetable/item/screen-renderer/custom-screens/uom/uom-screen';
import { uomConfiguration } from 'app/area/codetable/item/screen-renderer/screen-configuration/uom';
import { MeasurementSystem } from 'app/area/codetable/item/code-pickers/measurement-system';
import {
  ScalarValuesScreen
} from 'app/area/codetable/item/screen-renderer/custom-screens/scalar-values/scalar-values-screen';
import { CodeResidualIcon } from 'app/area/codetable/item/code-fields/code-residual-icon';
import {
  scalarValuesConfiguration
} from 'app/area/codetable/item/screen-renderer/custom-screens/scalar-values/scalar-values-configuration';
import { severityConfiguration } from 'app/area/codetable/item/screen-renderer/screen-configuration/severity-configuration';
import { SeverityScreen } from 'app/area/codetable/item/screen-renderer/custom-screens/severity/severity-screen';

/*
 * Use ScreenTemplate as 'component' property if the standard screen template with slots is enough, 
 * if it's not then use your fully custom screen.
*/
export const supportedScreens: CodetableScreen[] = [
  {
    id: SupportedCodeScreens.ConsequencesSeverity,
    name: CodetableScreenName.Consequence,
    component: ConsequencesScreen,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [Severity]
      },
      [CodeEditSectionType.ScalarValues]: {
        top: [Low, Mid, High, UoM]
      }
    },
    screenConfiguration: consequencesSeverityConfiguration,
    screenProps: {
      codeCategoryLabel: (translation: CodetableTranslation) => translation.consequence_type,
      scalarValuesInfo: (translation: CodetableTranslation) => translation.consequences_severity_scalar_values_info
    },
    validate: validateConsequenceSeverity
  },
  {
    id: SupportedCodeScreens.ConsequencesRecurrence,
    name: CodetableScreenName.Consequence,
    component: ConsequencesScreen,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [Recurrence]
      },
      [CodeEditSectionType.ScalarValues]: {
        top: [Low, Mid, High]
      }
    },
    screenConfiguration: consequencesRecurrenceConfiguration,
    screenProps: {
      codeCategoryLabel: (translation: CodetableTranslation) => translation.consequence_type,
      scalarValuesInfo: (translation: CodetableTranslation) => translation.consequences_recurrence_scalar_values_info
    },
    validate: validateConsequenceRecurrence
  },
  {
    id: SupportedCodeScreens.ConsequencesLikelihood,
    name: CodetableScreenName.Consequence,
    component: ConsequencesScreen,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [Likelihood]
      },
      [CodeEditSectionType.ScalarValues]: {
        top: [Low, Mid, High]
      }
    },
    screenConfiguration: consequencesLikelihoodConfiguration,
    screenProps: {
      codeCategoryLabel: (translation: CodetableTranslation) => translation.consequence_type,
      scalarValuesInfo: (translation: CodetableTranslation) => translation.consequences_likelihood_scalar_values_info
    },
    validate: validateConsequenceLikelihood
  },
  {
    id: SupportedCodeScreens.ChecklistHierarchy,
    name: CodetableScreenName.CheckListHierarchy,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [ScoreCalcWeight]
      }
    },
    validate: validateChecklistHierarchy
  },
  {
    id: SupportedCodeScreens.Company,
    name: CodetableScreenName.Company,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [CompanyRegisterId]
      }
    }
  },
  {
    id: SupportedCodeScreens.CauseCategory,
    name: CodetableScreenName.CauseCategory,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [MinLevel]
      }
    }
  },
  {
    id: SupportedCodeScreens.InteractionOption,
    name: CodetableScreenName.InteractionOption,
    component: ScreenTemplate,
    screenProps: {
      codeCategoryLabel: (translation: CodetableTranslation) => translation.interaction
    }
  },
  {
    id: SupportedCodeScreens.Reference,
    name: CodetableScreenName.Reference,
    component: ReferenceScreen,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [CheckedCode, UncheckedCode]
      }
    },
    validate: validateReference
  },
  {
    id: SupportedCodeScreens.RecurrenceFrequency,
    name: CodetableScreenName.RecurrenceFrequency,
    component: ScalarValuesScreen,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [RecurrenceLevel]
      },
      [CodeEditSectionType.ScalarValues]: {
        top: [Low, Mid, High]
      }
    },
    screenConfiguration: scalarValuesConfiguration,
    screenProps: {
      scalarValuesInfo: (translation: CodetableTranslation) => translation.recurrence_frequency_scalar_values_info,
      addInfoText: (translation: CodetableTranslation) => translation.risk_matrix_add_reminder,
      editInfoText: (translation: CodetableTranslation) => translation.risk_matrix_recurrence_frequency_reminder
    },
    validate: validateLevel
  },
  {
    id: SupportedCodeScreens.Severity,
    name: CodetableScreenName.Severity,
    component: SeverityScreen,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [SeverityLevel]
      }
    },
    screenProps: {
      addInfoText: (translation: CodetableTranslation) => translation.risk_matrix_add_reminder,
      editInfoText: (translation: CodetableTranslation) => translation.risk_matrix_severity_reminder
    },
    screenConfiguration: severityConfiguration,
    validate: validateLevel
  },
  {
    id: SupportedCodeScreens.Likelihood,
    name: CodetableScreenName.Likelihood,
    component: ScalarValuesScreen,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [LikelihoodLevel]
      },
      [CodeEditSectionType.ScalarValues]: {
        top: [Low, Mid, High]
      }
    },
    screenConfiguration: scalarValuesConfiguration,
    screenProps: {
      scalarValuesInfo: (translation: CodetableTranslation) => translation.likelihood_scalar_values_info,
      addInfoText: (translation: CodetableTranslation) => translation.risk_matrix_add_reminder,
      editInfoText: (translation: CodetableTranslation) => translation.risk_matrix_likelihood_reminder
    },
    validate: validateLevel
  },
  {
    id: SupportedCodeScreens.IntegrityStatus,
    name: CodetableScreenName.IntegrityStatus,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        bottom: [RangeFrom, RangeTo]
      }
    }
  },
  {
    id: SupportedCodeScreens.Evaluation,
    name: CodetableScreenName.Evaluation,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [ScoreCalcWeight]
      }
    }
  },
  {
    id: SupportedCodeScreens.SubstanceHierarchy,
    name: CodetableScreenName.SubstanceHierarchy,
    component: SubstanceHierarchyTemplate,
    screenConfiguration: substanceHierarchyConfiguration,
    screenSlots: {
      [CodeEditSectionType.UnitOfMeasureDetails]: {
        top: [DefaultUoMType, DefaultUoM]
      },
      [CodeEditSectionType.UnitOfMeasureSets]: {
        top: [UomSets]
      }
    },
    validate: validateSubstanceHierarchy
  },
  {
    id: SupportedCodeScreens.SubstanceCategory,
    name: CodetableScreenName.SubstanceCategory,
    component: SubstanceCategoryTemplate,
    screenConfiguration: substanceCategoryConfiguration,
    screenSlots: {
      [CodeEditSectionType.UnitOfMeasureSets]: {
        top: [UomSets]
      }
    },
    validate: validateSubstanceCategory
  },
  {
    id: SupportedCodeScreens.UnitOfMeasureSet,
    name: CodetableScreenName.UnitOfMeasureSet,
    component: UomSetTemplate,
    screenConfiguration: uomSetConfiguration
  },
  {
    id: SupportedCodeScreens.UnitOfMeasureConversionType,
    name: CodetableScreenName.UnitOfMeasureConversionType,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [UomTypeFrom, UomTypeTo]
      }
    },
    validate: validateUnitOfMeasureConversionType
  },
  {
    id: SupportedCodeScreens.InsuranceType,
    name: CodetableScreenName.InsuranceType,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [InsuranceTypeConsequenceType]
      }
    }
  },
  {
    id: SupportedCodeScreens.SecurityBreachType,
    name: CodetableScreenName.SecurityBreachType,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        top: [Severity]
      }
    },
    screenProps: {
      isSeverityRequired: false
    }
  },
  {
    id: SupportedCodeScreens.UnitOfMeasure,
    name: CodetableScreenName.UnitOfMeasure,
    component: UomTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        bottom: [MeasurementSystem]
      }
    },
    screenConfiguration: uomConfiguration,
    validate: validateUom
  },
  {
    id: SupportedCodeScreens.RiskArea,
    name: CodetableScreenName.RiskArea,
    component: ScreenTemplate,
    screenSlots: {
      [CodeEditSectionType.Details]: {
        bottom: [CodeResidualIcon, Recurrence, Severity, Likelihood]
      }
    },
    screenProps: {
      recurrenceLabel: (translation: CodetableTranslation) => translation.recurrence_default_basic,
      severityLabel: (translation: CodetableTranslation) => translation.potential_hazard_basic,
      likelihoodLabel: (translation: CodetableTranslation) => translation.likelihood_default_basic,
      isRecurrenceRequired: false,
      isSeverityRequired: false,
      isLikelihoodRequired: false
    }
  }
];
